// frontend/src/components/AudienceView/AudiencesPage.js
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
// This line should contain all your firestore imports for this file
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getProjectAudienceActorsPath, getProjectAudiencesPath, getProjectPath } from '../../utils/collectionPaths';
import AudienceManagementSection from '../ProjectView/AudienceManagementSection';
import ActorManagementSection from '../ProjectView/ActorManagementSection';
import LoadingSpinner from '../Common/LoadingSpinner';

// MUI Imports for better styling
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

// ... rest of the component

function AudiencesPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const [project, setProject] = useState(null); // Optional: fetch project details if needed (e.g., for project name)
  const [audiences, setAudiences] = useState([]);
  const [actorsByAudience, setActorsByAudience] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchProjectName = useCallback(async () => { // Optional: if you want to display project name
    if (!currentUser || !projectId) return;
    try {
        const projectRef = doc(db, getProjectPath(projectId));
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
            setProject({ id: projectSnap.id, ...projectSnap.data() });
        }
    } catch (err) {
        console.error("Failed to fetch project name for audiences page:", err);
    }
  }, [currentUser, projectId]);


  const fetchAudiencesAndTheirActors = useCallback(async () => {
    if (!currentUser || !projectId) return;
    setLoading(true);
    setError('');
    try {
      const audiencesCol = collection(db, getProjectAudiencesPath(projectId));
      const audiencesQuery = query(audiencesCol, orderBy('creationDate', 'desc')); // Or any order you prefer
      const audienceSnapshot = await getDocs(audiencesQuery);
      const fetchedAudiences = audienceSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setAudiences(fetchedAudiences);

      const newActorsByAudience = {};
      for (const aud of fetchedAudiences) {
        const actorsCol = collection(db, getProjectAudienceActorsPath(projectId, aud.id));
        const actorsQuery = query(actorsCol, orderBy('creationDate', 'desc'));
        const actorSnapshot = await getDocs(actorsQuery);
        newActorsByAudience[aud.id] = actorSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      }
      setActorsByAudience(newActorsByAudience);
    } catch (err) {
      console.error("Error fetching audiences/actors: ", err);
      setError('Failed to load audience or actor data.');
    }
    setLoading(false);
  }, [currentUser, projectId]);

  useEffect(() => {
    // fetchProjectName(); // Call if you need project name
    fetchAudiencesAndTheirActors();
  }, [fetchAudiencesAndTheirActors]); // Removed fetchProjectName from deps if not critical path for audiences

  const handleDataChange = () => { // To refresh data on this page after CRUD
    fetchAudiencesAndTheirActors();
  };
  if (loading) return <LoadingSpinner />;
  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Link to={`/project/${projectId}`} style={{ textDecoration: 'none' }}>← Back to Project Description</Link>
      </Box>
      
      <Typography variant="h4" component="h2" gutterBottom>
        Audience Management for Project {project ? project.projectName : ''}
      </Typography>

      <Paper elevation={0} sx={{ p: {xs: 1, sm: 2, md: 3}, mb: 4 }}>
        <AudienceManagementSection
          projectId={projectId}
          audiences={audiences} // Pass fetched audiences
          onAudiencesChange={handleDataChange} // Callback to refresh this page's data
          currentUser={currentUser}
          project={project} // Pass the project object
          fetchProjectData={fetchProjectName} // Pass the fetchProjectName function
          clearProjectTaskStatus={() => {}} // Empty function as placeholder
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </Paper>

      <Box sx={{ my: 4, height: '1px', backgroundColor: 'divider' }} />

      <Typography variant="h5" component="h3" gutterBottom>
        Actors/Personas
      </Typography>
      
      {audiences.length > 0 ? audiences.map(audience => (
        <Paper key={audience.id} elevation={0} variant="outlined" sx={{ mt: 3, p: 2 }}>
          <ActorManagementSection
            projectId={projectId}
            audienceId={audience.id}
            audienceName={audience.name}
            actors={actorsByAudience[audience.id] || []}
            onActorsChange={handleDataChange} // Refresh actors list on this page
            projectInterviewQuestions={project?.interviewQuestions} // Pass project specific questions if project state is fetched
            currentUser={currentUser}
            selectedActorsForInterview={new Map()} // Provides empty Map to avoid errors
            onToggleActorSelection={() => {}} // Empty function to avoid errors
            isGlobalTaskRunning={false} // Default value to avoid errors
          />
        </Paper>
      )) : (
        <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
          No audiences available. If you just created them, they might still be loading or an error occurred.
        </Typography>
      )}
    </Box>
  );
}
// You might need to import doc, getDoc from firestore if you use fetchProjectName
export default AudiencesPage;